import React, { useCallback, useState } from "react";

import { useDropzone } from "react-dropzone";
import Button from "components/Button/Button.tsx";

import "./UploadFile.scss";
import { setIsFileLoading } from "reducers/main";
import { useDispatch, useSelector } from "utils/redux.ts";

interface KaboodleUploadFileProps {
  onUpload?: (file: File) => void;
}

const UploadButton = ({
  handleUpload,
  file,
  isLoading,
  uploadError,
  onClearError,
}) => {
  return (
    <div className="upload-button-container">
      <Button
        className="upload-button"
        onClick={handleUpload}
        disabled={!file || isLoading}
        type="button"
      >
        {uploadError ? "Try Again" : isLoading ? "Uploading..." : "Upload"}
      </Button>

      {uploadError && (
        <p className="error-message">
          {uploadError} <button onClick={onClearError}>Clear</button>
        </p>
      )}
    </div>
  );
};

const UploadFile: React.FC<KaboodleUploadFileProps> = ({ onUpload }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const isLoading = useSelector((state) => state.main.isFileLoading);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadError(null); // Clear error when a new file is dropped
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleUpload = async () => {
    dispatch(setIsFileLoading(true));
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      onUpload(file);
    }
  };

  const handleClearError = () => {
    setUploadError(null);
  };

  return (
    <div className="kaboodle-upload-file">
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? "active" : ""}`}
      >
        <input {...getInputProps()} />
        {file ? (
          <p>
            <b>Selected file:</b> {file.name}
          </p>
        ) : (
          <p>
            <b>Drag & Drop</b> or <b>Click</b> to Select File
          </p>
        )}
      </div>

      <UploadButton
        handleUpload={handleUpload}
        file={file}
        isLoading={isLoading}
        uploadError={uploadError}
        onClearError={handleClearError}
      />
    </div>
  );
};

export default UploadFile;
